import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authApi, { TutorialResponse, VideoResponse } from '../../services/authApi';
import './tool-page.css';
import './CourseTutorialPage.css';

const CourseTutorialPage: React.FC = () => {
  const { tutorialId } = useParams<{ tutorialId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tutorial, setTutorial] = useState<TutorialResponse | null>(
    (location.state as any)?.tutorial ?? null
  );
  const [videos, setVideos] = useState<VideoResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState<VideoResponse | null>(null);
  const playerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = Number(tutorialId);
    if (!id) return;

    const loadVideos = async (tut: TutorialResponse) => {
      try {
        const res = await authApi.getTutorialVideos(id);
        const data = res.data ?? [];
        // Backend already nulls videoUrl for locked videos — trust it directly
        setVideos(data);
        const first = data.find((v) => !v.locked && v.videoUrl);
        if (first) setPlaying(first);
      } catch {
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    if (tutorial) {
      loadVideos(tutorial);
    } else {
      authApi.getTutorials()
        .then((res) => {
          const found = (res.data ?? []).find((t) => t.id === id) ?? null;
          setTutorial(found);
          if (found) loadVideos(found);
          else setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [tutorialId]);

  if (!loading && !tutorial) {
    return (
      <div className="tool-page">
        <div className="ctp-not-found">
          <p>Learning not found.</p>
          <button onClick={() => navigate(-1)}>← Back</button>
        </div>
      </div>
    );
  }

  const totalMinutes = videos.reduce((s, v) => s + (v.durationMin || 0), 0);

  return (
    <div className="tool-page ctp-page">
      {/* Back */}
      <button className="ctp-back" onClick={() => navigate(-1)}>
        ← Back
      </button>

      {tutorial && (
        <div className="ctp-hero">
          <div className="ctp-hero-top">
            <span className="ctp-badge">{tutorial.badge}</span>
            <div className="ctp-title">{tutorial.title}</div>
          </div>
          <div className="ctp-hero-info">
            <p className="ctp-desc">{tutorial.description}</p>
            <div className="ctp-meta">
              <span className={`pill ${tutorial.isFree ? 'pill-free' : 'pill-pro'}`}>
                {tutorial.isFree ? 'Free' : 'Pro'}
              </span>
              <span className="ctp-meta-txt">{tutorial.videoCount} video{tutorial.videoCount !== 1 ? 's' : ''}</span>
              {totalMinutes > 0 && <span className="ctp-meta-txt">{totalMinutes} min total</span>}
              {tutorial.tags.map((tag) => (
                <span key={tag} className="pill pill-tag">{tag.trim()}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="ctp-body">
        {/* Player */}
        <div className="ctp-player-col" ref={playerRef}>
          {playing && playing.videoUrl ? (
            <div className="ctp-player-wrap">
              <iframe
                src={playing.videoUrl + '?autoplay=1'}
                title={playing.title}
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="ctp-no-video">
              {loading ? 'Loading…' : 'Select a video to start watching'}
            </div>
          )}
          {playing && (
            <div className="ctp-now-playing">
              <div className="ctp-np-title">{playing.title}</div>
              {playing.description && <div className="ctp-np-desc">{playing.description}</div>}
            </div>
          )}
        </div>

        {/* Video list */}
        <div className="ctp-list-col">
          <div className="ctp-list-head">Learning Contents</div>
          {loading ? (
            <div className="ctp-list-loading">Loading videos…</div>
          ) : videos.length === 0 ? (
            <div className="ctp-list-empty">No videos available yet.</div>
          ) : (
            videos.map((v, idx) => (
              <div
                key={v.id}
                className={`ctp-item ${playing?.id === v.id ? 'ctp-item--active' : ''} ${v.locked ? 'ctp-item--locked' : ''}`}
                onClick={() => {
                  if (!v.locked && v.videoUrl) {
                    setPlaying(v);
                    if (playerRef.current) {
                      const top = playerRef.current.getBoundingClientRect().top + window.scrollY - 72;
                      window.scrollTo({ top, behavior: 'smooth' });
                    }
                  }
                }}
              >
                <div className="ctp-item-num">{idx + 1}</div>
                <div className="ctp-item-body">
                  <div className="ctp-item-title">{v.title}</div>
                  {v.description && <div className="ctp-item-desc">{v.description}</div>}
                </div>
                <div className="ctp-item-right">
                  {v.locked
                    ? <LockIcon />
                    : <span className="ctp-dur">{v.durationMin}m</span>
                  }
                </div>
              </div>
            ))
          )}

          {!tutorial?.isFree && !user?.isPremium && (
            <div className="ctp-upgrade-bar">
              🔒 Upgrade to Pro to unlock all videos
              <a href="/pricing" className="ctp-upgrade-btn">Upgrade ↗</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseTutorialPage;

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}
